import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandIcon,
  Inventory2Outlined as LotIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import NumpadInput from '../components/NumpadInput';
import { locationLabel } from '../utils';

// A chip that opens a menu of options; warning colour until chosen. Outlined so
// it doesn't compete with the solid confirm button
function PickerChip({ icon, label, placeholder, options, onPick, testId }) {
  const [anchor, setAnchor] = useState(null);
  const clickable = options.length > 1 || !label;
  return (
    <>
      <Chip
        icon={icon}
        data-testid={testId}
        label={label || placeholder}
        color={label ? 'default' : 'warning'}
        variant="outlined"
        deleteIcon={<ExpandIcon />}
        onDelete={clickable ? e => setAnchor(e.currentTarget.parentElement) : undefined}
        onClick={clickable ? e => setAnchor(e.currentTarget) : undefined}
        sx={{ height: 44, fontSize: '0.95rem', maxWidth: '100%' }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map(opt => (
          <MenuItem key={opt.key} sx={{ minHeight: 48 }} onClick={() => { onPick(opt); setAnchor(null); }}>
            {opt.menuLabel}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function QuantityPhase({
  direction,
  item,
  matchedLot,
  lots,
  lotTracked,
  stockLevels,
  allLocations,
  defaultLocationId,
  saving,
  error,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation(['inventory', 'common']);
  const isUse = direction === 'use';
  const uom = item?.uom ?? '';

  const locationOptions = useMemo(() => {
    if (isUse) {
      return stockLevels
        .filter(b => (b.quantity ?? 0) > 0)
        .sort((a, b) => b.quantity - a.quantity)
        .map(b => ({
          key: b.location_id ?? 'none',
          id: b.location_id ?? null,
          name: locationLabel(b, t),
          qty: b.quantity,
          menuLabel: `${locationLabel(b, t)} · ${b.quantity} ${uom}`,
        }));
    }
    return allLocations.map(l => ({ key: l.stock_location_id, id: l.stock_location_id, name: l.name, menuLabel: l.name }));
  }, [isUse, stockLevels, allLocations, uom, t]);

  // Preselect so the common case is one tap: the scanner's default location,
  // otherwise (use) where most stock is, or (add) the only location there is
  const [location, setLocation] = useState(() => (
    locationOptions.find(o => o.id && o.id === defaultLocationId)
    ?? (isUse || locationOptions.length === 1 ? locationOptions[0] : undefined)
  ));

  // Adding to a lot-tracked item needs a lot. Use without one takes the oldest first.
  const needsLotPick = !isUse && lotTracked && !matchedLot;
  const lotOptions = lots.map(l => ({
    key: l.lot_id,
    id: l.lot_id,
    name: l.lot_number,
    menuLabel: l.expiry_date ? `${l.lot_number} · ${l.expiry_date}` : l.lot_number,
  }));
  const [lot, setLot] = useState(() => (needsLotPick && lotOptions.length === 1 ? lotOptions[0] : undefined));

  const [qty, setQty] = useState('1');
  const numQty = parseInt(qty, 10) || 0;

  const maxQty = isUse
    ? Math.min(location?.qty ?? 0, matchedLot?.quantity_on_hand ?? Infinity)
    : Infinity;
  const needsLocation = locationOptions.length > 0 && !location;
  const overMax = isUse && numQty > maxQty;
  const noStock = isUse && locationOptions.length === 0;
  const valid = numQty > 0 && !overMax && !needsLocation && !noStock && (!needsLotPick || lot);

  const handleConfirm = () => onConfirm({
    qty: numQty,
    locationId: location?.id ?? null,
    locationName: location?.name ?? null,
    lotId: lot?.id ?? null,
  });

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1 }}>
        <IconButton onClick={onCancel} aria-label={t('common:buttons.cancel')} sx={{ width: 48, height: 48 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {isUse ? t('inventory:scanner.use_title') : t('inventory:scanner.add_title')}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} noWrap>{item?.name}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, py: 1 }}>
        {locationOptions.length > 0 && (
          <PickerChip
            testId="location-chip"
            icon={<PlaceIcon />}
            label={location ? (isUse ? `${location.name} · ${location.qty}` : location.name) : null}
            placeholder={t('inventory:scanner.pick_location')}
            options={locationOptions}
            onPick={setLocation}
          />
        )}
        {matchedLot && (
          <Chip icon={<LotIcon />} variant="outlined" label={t('inventory:scanner.lot_label', { lot: matchedLot.lot_number })} sx={{ height: 44 }} />
        )}
        {needsLotPick && lotOptions.length > 0 && (
          <PickerChip
            testId="lot-chip"
            icon={<LotIcon />}
            label={lot ? t('inventory:scanner.lot_label', { lot: lot.name }) : null}
            placeholder={t('inventory:scanner.pick_lot')}
            options={lotOptions}
            onPick={setLot}
          />
        )}
        {isUse && lotTracked && !matchedLot && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('inventory:scanner.oldest_lot_first')}
          </Typography>
        )}
      </Box>

      {noStock && <Alert severity="warning" sx={{ mx: 2 }}>{t('inventory:scanner.no_stock_to_use')}</Alert>}
      {needsLotPick && lotOptions.length === 0 && (
        <Alert severity="info" sx={{ mx: 2 }}>{t('inventory:scanner.scan_lot_to_add')}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mx: 2 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography data-testid="qty-display" sx={{ fontSize: 56, fontWeight: 700, lineHeight: 1.1 }} color={overMax ? 'error.main' : 'text.primary'}>
          {qty || '0'}
          <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>{uom}</Typography>
        </Typography>
        {overMax && (
          <Typography variant="caption" color="error.main">
            {t('inventory:scanner.max_available', { count: maxQty, uom })}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <NumpadInput value={qty} onChange={setQty} disabled={saving} />
        <Button
          variant="contained"
          color={isUse ? 'primary' : 'success'}
          size="large"
          onClick={handleConfirm}
          disabled={!valid || saving}
          sx={{ minHeight: 56, borderRadius: 2, fontSize: '1.1rem', mt: 0.5 }}
        >
          {saving
            ? <CircularProgress size={24} color="inherit" />
            : t(isUse ? 'inventory:scanner.confirm_use' : 'inventory:scanner.confirm_add', { count: numQty, uom })}
        </Button>
      </Box>
    </Box>
  );
}
